import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// Integration tiles data with logo images
const integrations = [
  {
    name: 'Clio',
    logo: '/integrations/clio.png',
  },
  {
    name: 'Filevine',
    logo: '/integrations/filevine.png',
  },
  {
    name: 'Litify',
    logo: '/integrations/litify.png',
  },
  {
    name: 'myCase',
    logo: '/integrations/mycase.png',
  },
  {
    name: 'Salesforce',
    logo: '/integrations/salesforce.png',
  },
  {
    name: 'Lawmatics',
    logo: '/integrations/lawmatics.png',
  },
  {
    name: 'RingCentral',
    logo: '/integrations/ringcentral.png',
  },
  {
    name: 'Zapier',
    logo: '/integrations/zapier.png',
  },
];

// Extra tiles that fade out (skeleton placeholders only)
const fadedIntegrations = [
  { color: '#7B68EE' },
  { color: '#00D4AA' },
];

function IntegrationTile({ integration, index }: { integration: typeof integrations[0], index: number }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        backgroundColor: '#FFFFFF',
        borderRadius: 2,
        border: '1px solid #E8E2D8',
        boxShadow: '0 2px 8px rgba(62, 48, 30, 0.06)',
        animation: `slideIn 0.4s ease-out ${index * 0.1}s both`,
        '@keyframes slideIn': {
          '0%': { opacity: 0, transform: 'translateY(10px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      {/* Logo */}
      <Box
        component="img"
        src={integration.logo}
        alt={integration.name}
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1.5,
          objectFit: 'contain',
          flexShrink: 0,
          backgroundColor: '#F7F3EB',
          p: 0.5,
        }}
      />
      
      {/* Name and status */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: '0.85rem',
            fontWeight: 600,
            color: '#3E301E',
            lineHeight: 1.2,
          }}
        >
          {integration.name}
        </Typography>
        <Typography
          component="span"
          sx={{
            fontSize: '0.7rem',
            color: '#27AE60',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Box
            component="span"
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: '#27AE60',
              display: 'inline-block',
            }}
          />
          Connected
        </Typography>
      </Box>
    </Box>
  );
}

export default function IntegrationsAnimation() {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minWidth: 0,
        backgroundColor: '#FDFCF9',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Mock Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          borderBottom: '1px solid #E8E2D8',
        }}
      >
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#FF5F57' }} />
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#FFBD2E' }} />
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#27C93F' }} />
        </Box>
        <Box
          sx={{
            flex: 1,
            mx: 2,
            py: 0.5,
            px: 2,
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            borderRadius: 1,
            fontSize: '0.75rem',
            color: 'text.secondary',
          }}
        >
          OpenIntake Dashboard
        </Box>
      </Box>

      {/* Content Area */}
      <Box sx={{ display: 'flex', flex: 1, position: 'relative', minWidth: 0 }}>
        {/* Sidebar */}
        <Box sx={{ width: 48, borderRight: '1px solid #E8E2D8', p: 1 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Box
              key={i}
              sx={{
                width: 28,
                height: 28,
                mb: 1,
                borderRadius: 1,
                backgroundColor: i === 3 ? '#B5683C' : 'rgba(0, 0, 0, 0.06)',
              }}
            />
          ))}
        </Box>

        {/* Main Content */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden', minWidth: 0 }}>
          {/* Header */}
          <Box sx={{ p: 2, pb: 1 }}>
            <Typography
              sx={{
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#3E301E',
                mb: 0.5,
              }}
            >
              Integrations
            </Typography>
            <Typography
              sx={{
                fontSize: '0.7rem',
                color: '#6B5F4D',
              }}
            >
              Connected to your existing tools
            </Typography>
          </Box>

          {/* Integration Tiles Grid */}
          <Box
            sx={{
              flex: 1,
              px: 2,
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 1.5,
              alignContent: 'start',
              pb: 0,
            }}
          >
            {integrations.map((integration, index) => (
              <IntegrationTile key={integration.name} integration={integration} index={index} />
            ))}
            
            {/* Faded placeholder tiles */}
            {fadedIntegrations.map((integration, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  backgroundColor: '#FFFFFF',
                  borderRadius: 2,
                  border: '1px solid #E8E2D8',
                  boxShadow: '0 2px 8px rgba(62, 48, 30, 0.06)',
                }}
              >
                {/* Colored icon placeholder */}
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    backgroundColor: integration.color,
                    flexShrink: 0,
                  }}
                />
                
                {/* Placeholder lines for text */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ height: 10, width: '70%', backgroundColor: '#E8E2D8', borderRadius: 1, mb: 0.5 }} />
                  <Box sx={{ height: 8, width: '50%', backgroundColor: '#E8E2D8', borderRadius: 1 }} />
                </Box>
              </Box>
            ))}
          </Box>

          {/* Fade overlay */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 120,
              background: 'linear-gradient(to bottom, rgba(253, 252, 249, 0) 0%, rgba(253, 252, 249, 0.9) 50%, rgba(253, 252, 249, 1) 100%)',
              pointerEvents: 'none',
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
